import type { TasklistPageJson } from '@/types/tasklistPageJson';
import type { TasklistDisplayRuntime } from '@/utils/tasklistPageDisplay';
import { shouldShowTasklistSlice } from '@/utils/tasklistPageDisplay';

/** LC row fields needed to render hub cards + navigate */
export interface LearningCenterHubSourceTask {
  id: number;
  title: string;
  description: string;
  taskType: 'anytime' | 'actionable';
  completed: boolean;
  order: number;
  pageId?: string;
  pathSegment?: string;
  group?: string;
  roles?: string[];
  taskId?: number;
}

export interface LearningCenterHubVisibleTask extends LearningCenterHubSourceTask {
  /** Resolved display title after placeholders */
  displayTitle: string;
  /** Resolved short description for card */
  displayDescription: string;
  featured: boolean;
}

export function buildLearningCenterHubVisibleTasks(
  sourceTasks: LearningCenterHubSourceTask[],
  pageByPageId: Map<string, TasklistPageJson>,
  options: {
    userRoleVal: string | undefined;
    gradeId: string | undefined;
    studentAge?: number;
    runtime: TasklistDisplayRuntime;
    applyPlaceholders: (text: string) => string;
  }
): LearningCenterHubVisibleTask[] {
  const { userRoleVal, gradeId, studentAge, runtime, applyPlaceholders } = options;
  const out: LearningCenterHubVisibleTask[] = [];

  for (const t of sourceTasks) {
    const pid = t.pageId?.trim();
    if (!pid) continue;

    const page = pageByPageId.get(pid);
    if (!page) continue;
    if (page.isVisible === false) continue;
    if (!shouldShowTasklistSlice(page.displayConditions, userRoleVal, gradeId, runtime, studentAge)) continue;

    const baseTitle = page.title?.trim() ? page.title : t.title;
    const rawDesc = (page.preview ?? page.description ?? t.description ?? '').trim();
    const baseDesc = rawDesc || t.description || '';

    out.push({
      ...t,
      displayTitle: applyPlaceholders(baseTitle),
      displayDescription: applyPlaceholders(baseDesc),
      featured: Boolean(page.featured),
    });
  }

  out.sort((a, b) => {
    const af = a.featured ? 1 : 0;
    const bf = b.featured ? 1 : 0;
    if (bf !== af) return bf - af;
    return a.order - b.order;
  });

  return out;
}

export function formatLearningCenterHubCount(template: string, completed: number, total: number): string {
  return template.replace(/\{completed\}/g, String(completed)).replace(/\{total\}/g, String(total));
}
